import React, { useEffect, useState } from 'react';
import { X, UtensilsCrossed } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { fetchJsonCached } from '../../lib/settingsCache';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Details pop-up shown when a logo is clicked. */
const ListingModal = ({ listing, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!listing) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={listing.name}
      data-testid="auto-rentals-modal"
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} data-testid="auto-rentals-modal-overlay" />

      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          aria-label="Close"
          data-testid="auto-rentals-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-8 py-10 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-2">
            <h2 className="text-2xl font-semibold text-slate-900" data-testid="auto-rentals-modal-name">
              {listing.name}
            </h2>
            {listing.phone && (
              <a
                href={`tel:${listing.phone.replace(/[^0-9+]/g, '')}`}
                className="text-2xl font-semibold text-slate-900 hover:text-red-600 transition-colors"
                data-testid="auto-rentals-modal-phone"
              >
                {listing.phone}
              </a>
            )}
          </div>

          {listing.description && (
            <p
              className="mt-6 text-slate-700 leading-relaxed whitespace-pre-line"
              data-testid="auto-rentals-modal-description"
            >
              {listing.description}
            </p>
          )}

          {listing.website_url && (
            <a
              href={listing.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-block text-red-600 font-bold tracking-wide uppercase hover:text-red-700 hover:underline"
              data-testid="auto-rentals-modal-website"
            >
              Visit Our Website
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const AutoRentalsPage = () => {
  const { auto_rentals_enabled, _loaded } = useSiteFeatureFlags();
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSeoMetadata({
      title: 'St John Auto Rentals | Jeep & Car Rental Companies | St John USVI',
      description:
        'Jeep, car and scooter rentals on St John, U.S. Virgin Islands - every rental company on the island in one place with phone numbers, hours and websites.',
      keywords:
        'st john car rental, st john jeep rental, cruz bay car rental, usvi jeep rentals, st john auto rental',
      canonicalPath: '/auto-rentals-st-john',
      ogType: 'website',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchJsonCached(`${API_URL}/api/public/auto-rentals`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setTabs(list);
        const firstWithContent = list.find((t) => t.count > 0) || list[0];
        setActiveTab(firstWithContent ? firstWithContent.key : '');
      } catch (error) {
        if (!cancelled) setTabs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const current = tabs.find((t) => t.key === activeTab);

  return (
    <div className="min-h-screen bg-white" data-testid="auto-rentals-page">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <header className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              Auto Rentals
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">St John, Virgin Islands</p>
          </header>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="auto-rentals-loading">
              <div className="w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !auto_rentals_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="auto-rentals-disabled">
              Our Auto Rentals directory is being updated. Please check back soon.
            </p>
          ) : (
            <>
              {/* category tabs */}
              <div className="flex flex-wrap border-b border-slate-300" data-testid="auto-rentals-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-3 text-sm font-bold tracking-wide border border-b-0 -mb-px transition-colors ${
                      activeTab === tab.key
                        ? 'bg-white text-red-600 border-slate-300'
                        : 'bg-slate-100 text-red-500 border-transparent hover:bg-slate-50'
                    }`}
                    data-testid={`auto-rentals-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {!current || current.sections.length === 0 ? (
                <p className="text-center text-slate-500 py-16" data-testid="auto-rentals-empty">
                  Listings for this category are coming soon.
                </p>
              ) : (
                current.sections.map((section) => (
                  <section key={section.key} className="pt-14" data-testid={`auto-rentals-section-${current.key}-${section.key}`}>
                    {section.title && (
                      <h2 className="font-script text-4xl sm:text-5xl text-red-600 text-center">
                        {section.title}
                      </h2>
                    )}
                    {section.note && (
                      <p className="font-script text-2xl text-slate-800 text-center mt-2 whitespace-pre-line">
                        {section.note}
                      </p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-12 mt-12">
                      {section.listings.map((listing) => (
                        <div key={listing.id} className="text-center" data-testid={`auto-rentals-card-${listing.slug}`}>
                          <button
                            onClick={() => setSelected(listing)}
                            className="group block w-full"
                            aria-label={`${listing.name} details`}
                            data-testid={`auto-rentals-logo-btn-${listing.slug}`}
                          >
                            <div className="h-32 sm:h-36 flex items-center justify-center px-2">
                              {listing.logo_url ? (
                                <img
                                  src={resolveMediaUrl(listing.logo_url)}
                                  alt={listing.name}
                                  loading="lazy"
                                  className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <span className="text-lg font-bold text-slate-800 group-hover:text-red-600 transition-colors">
                                  {listing.name}
                                </span>
                              )}
                            </div>
                            <span className="mt-4 block mx-auto w-24 border-t-2 border-blue-700/70" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </>
          )}
        </div>
      </main>

      {selected && <ListingModal listing={selected} onClose={() => setSelected(null)} />}

      <Footer />
    </div>
  );
};

export default AutoRentalsPage;
